import { useEffect } from 'react';
import { NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { MOBILE_TABS, ROLE_LABELS, VIEWS, type ViewId } from '@ouradent/shared';
import { useSession } from '../auth/SessionProvider';
import { useCalls, useRealtime, useTasks } from '../lib/data';
import { initials } from '../lib/format';
import { Icon, type IconName } from '../ui/icons';
import { useDrawer } from '../ui/kit';
import { DrawerHost } from './DrawerHost';

export function AppShell() {
  const { me, can } = useSession();
  const { open } = useDrawer();
  useRealtime(Boolean(me));

  const calls = useCalls(can('calls'));
  const tasks = useTasks(can('tasks') || can('pipeline'));
  useDeepLink();

  if (!me) return null;

  const allowed = VIEWS.filter((view) => can(view.perm));
  const grouped = allowed.length > 4;

  const review = (calls.data ?? []).filter(
    (c) => c.status === 'review' || c.status === 'unlinked',
  ).length;
  const overdue = (tasks.data ?? []).filter(
    (t) => !t.done_at && t.owner_id === me.id && new Date(t.due_at) < new Date(),
  ).length;
  const badge = (id: ViewId) =>
    id === 'calls' && review ? (
      <span className="badge purple" aria-hidden="true">
        {review}
      </span>
    ) : id === 'tasks' && overdue ? (
      <span className="badge yellow" aria-hidden="true">
        {overdue}
      </span>
    ) : null;

  // The external caller's one screen is his queue, so it is named that way.
  const label = (view: (typeof VIEWS)[number]) =>
    view.id === 'coldcall' && me.role === 'caller' ? 'My call queue' : view.label;

  const tabs = (MOBILE_TABS[me.role] ?? [])
    .filter((id) => allowed.some((v) => v.id === id))
    .slice(0, 4)
    .map((id) => VIEWS.find((v) => v.id === id)!);

  const grad = 'linear-gradient(135deg, #6E4BF2, #22D3C5)';
  let lastGroup = '';

  return (
    <>
      {!me.external && <div className="bgfx" aria-hidden="true" />}
      <div className={`app${me.external ? ' ccompact' : ''}`}>
        <aside className="side" aria-label="Sidebar">
          <a className="logo" href="/today">
            <Icon name="logo" />
            <b>Ouradent</b>
          </a>
          {can('pipeline') && (
            <button className="searchbtn" onClick={() => open({ type: 'search' })}>
              <Icon name="search" />
              <span>Search leads</span>
              <kbd>⌘K</kbd>
            </button>
          )}
          <nav className="nav" aria-label="Main">
            {allowed.map((view) => {
              const header = grouped && view.group !== lastGroup ? view.group : null;
              lastGroup = view.group;
              return (
                <div key={view.id} style={{ display: 'contents' }}>
                  {header && <div className="navgrp">{header}</div>}
                  <NavLink to={`/${view.id}`} className="navlink">
                    <Icon name={view.id as IconName} />
                    <span>{label(view)}</span>
                    {badge(view.id)}
                  </NavLink>
                </div>
              );
            })}
          </nav>
          <button className="bellbtn" onClick={() => open({ type: 'notifications' })}>
            <Icon name="bell" />
            <span>Notifications</span>
          </button>
          <div className="side-foot">
            <div className="me">
              <span className="ph" style={{ background: grad }}>
                {initials(me.fullName)}
              </span>
              <div>
                <div style={{ font: '500 16px var(--font)' }}>{me.fullName}</div>
                <div style={{ fontSize: 12, color: 'var(--soft)' }}>{ROLE_LABELS[me.role]}</div>
              </div>
            </div>
            <SignOut />
          </div>
        </aside>

        <div className="main">
          <main id="view">
            <Outlet />
          </main>
        </div>
      </div>

      <nav
        className="tabbar"
        aria-label="Sections"
        style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, 1fr)` }}
      >
        {tabs.map((view) => (
          <NavLink key={view.id} to={`/${view.id}`}>
            <Icon name={view.id as IconName} />
            <span>{label(view)}</span>
            {badge(view.id)}
          </NavLink>
        ))}
        <button onClick={() => open({ type: 'menu' })} aria-label="Menu">
          <span className="pav" style={{ background: grad }}>
            {initials(me.fullName)}
          </span>
          <span>Menu</span>
        </button>
      </nav>

      <DrawerHost />
    </>
  );
}

function SignOut() {
  const { signOut } = useSession();
  return (
    <button className="linkbtn" onClick={() => void signOut()}>
      Sign out
    </button>
  );
}

/** Notification links (?lead=, ?call=, ?event=) open their drawer on arrival. */
function useDeepLink() {
  const [params, setParams] = useSearchParams();
  const { open } = useDrawer();
  useEffect(() => {
    const lead = params.get('lead');
    const call = params.get('call');
    const event = params.get('event');
    if (lead) open({ type: 'lead', id: lead });
    else if (call) open({ type: 'call', id: call });
    else if (event) open({ type: 'event', id: event });
    else return;
    setParams({}, { replace: true });
  }, [params, open, setParams]);
}
